import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { SITE_ONLY, SITE_VENTE } from "./lib/siteRouting";
import { MOBILE_CLIENT, MOBILE_STAFF } from "./lib/mobileRouting";
import ScrollToTop from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { ClientAuthProvider, useClientAuth } from "./contexts/ClientAuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PortailProtectedRoute from "./components/portail/PortailProtectedRoute";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Immeubles from "./pages/Immeubles";
import ImmeubleDetail from "./pages/ImmeubleDetail";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Contrats from "./pages/Contrats";
import ContratDetail from "./pages/ContratDetail";
import Paiements from "./pages/Paiements";
import EncaissementRapide from "./pages/EncaissementRapide";
import Rappels from "./pages/Rappels";
import Caisse from "./pages/Caisse";
import Rapports from "./pages/Rapports";
import Utilisateurs from "./pages/Utilisateurs";
import AuditLog from "./pages/AuditLog";
import Parametres from "./pages/Parametres";
import Pipeline from "./pages/Pipeline";
import Tickets from "./pages/Tickets";
import Agents from "./pages/Agents";
import Souscriptions from "./pages/Souscriptions";
import SiteHome from "./pages/site/Home";
import SiteProjets from "./pages/site/Projets";
import SiteProjetDetail from "./pages/site/ProjetDetail";
import SiteContact from "./pages/site/Contact";
import SiteQuiSommesNous from "./pages/site/QuiSommesNous";
import SiteEquipements from "./pages/site/Equipements";
import SiteSouscription from "./pages/site/Souscription";
import PortailLogin from "./pages/portail/Login";
import PortailMagic from "./pages/portail/Magic";
import PortailFirstPassword from "./pages/portail/FirstPassword";
import PortailDashboard from "./pages/portail/Dashboard";
import PortailContrats from "./pages/portail/Contrats";
import PortailContratDetail from "./pages/portail/ContratDetail";
import PortailPaiements from "./pages/portail/Paiements";
import PortailParametres from "./pages/portail/Parametres";
import PortailMessages from "./pages/portail/Messages";
import MobileLogin from "./pages/mobile/MobileLogin";
import MobileDashboard from "./pages/mobile/MobileDashboard";
import MobileBiens from "./pages/mobile/MobileBiens";
import MobileChantier from "./pages/mobile/MobileChantier";
import MobilePaiements from "./pages/mobile/MobilePaiements";
import MobileMessages from "./pages/mobile/MobileMessages";
import MobileProfil from "./pages/mobile/MobileProfil";
import VenteCatalogue from "./pages/vente/VenteCatalogue";
import VenteBienDetail from "./pages/vente/VenteBienDetail";
import StaffDashboard from "./pages/mobile-staff/StaffDashboard";
import StaffProspects from "./pages/mobile-staff/StaffProspects";
import StaffClients from "./pages/mobile-staff/StaffClients";
import StaffBiens from "./pages/mobile-staff/StaffBiens";
import StaffEncaissement from "./pages/mobile-staff/StaffEncaissement";
import StaffProfil from "./pages/mobile-staff/StaffProfil";
import CreditSimulator from "./pages/CreditSimulator";
import CreditLeads from "./pages/CreditLeads";
import Securite from "./pages/Securite";
import Documents from "./pages/Documents";
import Sauvegardes from "./pages/Sauvegardes";
import CompanySettings from "./pages/CompanySettings";

/**
 * Build mode SITE-ONLY (REACT_APP_SITE_ONLY=true) :
 *   → uniquement le site public, monté à la racine. Aucune route admin/portail.
 *   → utilisé par le container `tpco_site` servi sur tpco.sn (ou tpco.sn).
 *
 * Build mode CRM (par défaut) :
 *   → routes admin + portail client + site public sous /site/*
 *   → utilisé par le container `tpco_frontend` servi sur app.tpco.sn.
 */
function MobileClientApp() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-center" richColors />
      <ClientAuthProvider>
        <Routes>
          <Route path="/login" element={<MobileGuestGate><MobileLogin /></MobileGuestGate>} />
          <Route path="/" element={<MobileAuthGate><MobileDashboard /></MobileAuthGate>} />
          <Route path="/biens" element={<MobileAuthGate><MobileBiens /></MobileAuthGate>} />
          <Route path="/chantier" element={<MobileAuthGate><MobileChantier /></MobileAuthGate>} />
          <Route path="/paiements" element={<MobileAuthGate><MobilePaiements /></MobileAuthGate>} />
          <Route path="/messages" element={<MobileAuthGate><MobileMessages /></MobileAuthGate>} />
          <Route path="/profil" element={<MobileAuthGate><MobileProfil /></MobileAuthGate>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ClientAuthProvider>
    </BrowserRouter>
  );
}

/** Garde d'accès : redirige vers /login si non authentifié. */
function MobileAuthGate({ children }) {
  const { client, loading } = useClientAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1A3D]">
        <img src="/logo-tpco.png" alt="TPCO" className="h-12 animate-pulse opacity-80 bg-white rounded-lg p-2" />
      </div>
    );
  }
  if (!client) return <Navigate to="/login" replace />;
  return children;
}

/** Inverse : redirige vers / si déjà connecté (évite de revoir le login). */
function MobileGuestGate({ children }) {
  const { client, loading } = useClientAuth();
  if (loading) return null;
  if (client) return <Navigate to="/" replace />;
  return children;
}

function VenteApp() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<VenteCatalogue />} />
        <Route path="/bien/:id" element={<VenteBienDetail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Build mode MOBILE STAFF (REACT_APP_MOBILE_STAFF=true) :
 *   PWA dédiée aux équipes terrain. Responsive mobile + tablette.
 *   Servi sur mobile.tpco.sn (container tpco_mobile_staff).
 */
function MobileStaffApp() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-center" richColors />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><StaffDashboard /></ProtectedRoute>} />
          <Route path="/prospects" element={<ProtectedRoute roles={["admin", "secretaire", "commercial"]}><StaffProspects /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><StaffClients /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
          <Route path="/biens" element={<ProtectedRoute><StaffBiens /></ProtectedRoute>} />
          <Route path="/immeubles/:id" element={<ProtectedRoute><ImmeubleDetail /></ProtectedRoute>} />
          <Route path="/encaissement" element={<ProtectedRoute roles={["admin", "secretaire", "comptable", "caissier"]}><StaffEncaissement /></ProtectedRoute>} />
          <Route path="/credit-sim" element={<ProtectedRoute><CreditSimulator /></ProtectedRoute>} />
          <Route path="/profil" element={<ProtectedRoute><StaffProfil /></ProtectedRoute>} />
          <Route path="/securite" element={<ProtectedRoute><Securite /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute roles={["admin", "secretaire", "commercial", "comptable"]}><Documents /></ProtectedRoute>} />
          <Route path="/sauvegardes" element={<ProtectedRoute roles={["admin"]}><Sauvegardes /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

function SiteOnlyApp() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<SiteHome />} />
        <Route path="/qui-sommes-nous" element={<SiteQuiSommesNous />} />
        <Route path="/equipements" element={<SiteEquipements />} />
        <Route path="/projets" element={<SiteProjets />} />
        <Route path="/projet/:slug" element={<SiteProjetDetail />} />
        <Route path="/contact" element={<SiteContact />} />
        <Route path="/souscription/:slug" element={<SiteSouscription />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function FullApp() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" richColors />
      <Routes>
        {/* PUBLIC WEBSITE — no auth */}
        <Route path="/site" element={<SiteHome />} />
        <Route path="/site/qui-sommes-nous" element={<SiteQuiSommesNous />} />
        <Route path="/site/equipements" element={<SiteEquipements />} />
        <Route path="/site/projets" element={<SiteProjets />} />
        <Route path="/site/projet/:slug" element={<SiteProjetDetail />} />
        <Route path="/site/contact" element={<SiteContact />} />
        <Route path="/souscription/:slug" element={<SiteSouscription />} />

        {/* PORTAIL CLIENT — own AuthProvider */}
        <Route path="/portail/*" element={
          <ClientAuthProvider>
            <Routes>
              <Route path="login" element={<PortailLogin />} />
              <Route path="magic/:token" element={<PortailMagic />} />
              <Route path="first-password" element={<PortailProtectedRoute><PortailFirstPassword /></PortailProtectedRoute>} />
              <Route path="" element={<PortailProtectedRoute><PortailDashboard /></PortailProtectedRoute>} />
              <Route path="contrats" element={<PortailProtectedRoute><PortailContrats /></PortailProtectedRoute>} />
              <Route path="contrats/:id" element={<PortailProtectedRoute><PortailContratDetail /></PortailProtectedRoute>} />
              <Route path="paiements" element={<PortailProtectedRoute><PortailPaiements /></PortailProtectedRoute>} />
              <Route path="messages" element={<PortailProtectedRoute><PortailMessages /></PortailProtectedRoute>} />
              <Route path="parametres" element={<PortailProtectedRoute><PortailParametres /></PortailProtectedRoute>} />
            </Routes>
          </ClientAuthProvider>
        } />

        {/* STAFF / ADMIN */}
        <Route path="*" element={
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/immeubles" element={<ProtectedRoute><Immeubles /></ProtectedRoute>} />
              <Route path="/immeubles/:id" element={<ProtectedRoute><ImmeubleDetail /></ProtectedRoute>} />
              <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
              <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
              <Route path="/contrats" element={<ProtectedRoute><Contrats /></ProtectedRoute>} />
              <Route path="/contrats/:id" element={<ProtectedRoute><ContratDetail /></ProtectedRoute>} />
              <Route path="/pipeline" element={<ProtectedRoute roles={["admin", "secretaire", "commercial"]}><Pipeline /></ProtectedRoute>} />
              <Route path="/tickets" element={<ProtectedRoute roles={["admin", "secretaire", "commercial", "comptable"]}><Tickets /></ProtectedRoute>} />
              <Route path="/agents" element={<ProtectedRoute roles={["admin", "secretaire"]}><Agents /></ProtectedRoute>} />
              <Route path="/souscriptions" element={<ProtectedRoute roles={["admin", "secretaire", "commercial"]}><Souscriptions /></ProtectedRoute>} />
              <Route path="/paiements" element={<ProtectedRoute roles={["admin", "secretaire", "comptable", "lecture"]}><Paiements /></ProtectedRoute>} />
              <Route path="/encaissement" element={<ProtectedRoute roles={["admin", "secretaire", "comptable", "caissier"]}><EncaissementRapide /></ProtectedRoute>} />
              <Route path="/caisse" element={<ProtectedRoute roles={["admin", "secretaire", "comptable", "caissier"]}><Caisse /></ProtectedRoute>} />
              <Route path="/credit/simulateur" element={<ProtectedRoute roles={["admin", "secretaire", "commercial", "comptable"]}><CreditSimulator /></ProtectedRoute>} />
              <Route path="/credit/prospects" element={<ProtectedRoute roles={["admin", "secretaire", "commercial"]}><CreditLeads /></ProtectedRoute>} />
              <Route path="/securite" element={<ProtectedRoute><Securite /></ProtectedRoute>} />
              <Route path="/documents" element={<ProtectedRoute roles={["admin", "secretaire", "commercial", "comptable"]}><Documents /></ProtectedRoute>} />
              <Route path="/sauvegardes" element={<ProtectedRoute roles={["admin"]}><Sauvegardes /></ProtectedRoute>} />
              <Route path="/reglages/signature" element={<ProtectedRoute roles={["admin"]}><CompanySettings /></ProtectedRoute>} />
              <Route path="/rappels" element={<ProtectedRoute roles={["admin", "secretaire", "comptable", "lecture"]}><Rappels /></ProtectedRoute>} />
              <Route path="/rapports" element={<ProtectedRoute roles={["admin", "secretaire", "comptable"]}><Rapports /></ProtectedRoute>} />
              <Route path="/utilisateurs" element={<ProtectedRoute roles={["admin"]}><Utilisateurs /></ProtectedRoute>} />
              <Route path="/parametres" element={<ProtectedRoute roles={["admin"]}><Parametres /></ProtectedRoute>} />
              <Route path="/audit" element={<ProtectedRoute roles={["admin"]}><AuditLog /></ProtectedRoute>} />
            </Routes>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  if (MOBILE_CLIENT) return <MobileClientApp />;
  if (MOBILE_STAFF) return <MobileStaffApp />;
  if (SITE_VENTE) return <VenteApp />;
  return SITE_ONLY ? <SiteOnlyApp /> : <FullApp />;
}
